import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, Heart, Share2, MapPin, Clock, Zap, Smartphone, Calendar, Shield, Check, ChevronLeft, ChevronRight, ChevronDown, Minus, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { activities, packageOptions, reviewsList, faqs } from '../mock';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import ActivityCard from '../components/ActivityCard';

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, wishlist, toggleWishlist } = useApp();
  const activity = useMemo(() => activities.find((a) => a.id === id) || activities[0], [id]);
  const [pkgId, setPkgId] = useState(packageOptions[0].id);
  const [qty, setQty] = useState(1);
  const [date, setDate] = useState('');
  const [imgIdx, setImgIdx] = useState(0);
  const images = [activity.image, ...activities.filter((a)=>a.id!==activity.id).slice(0,4).map((a)=>a.image)];
  const pkg = packageOptions.find((p) => p.id === pkgId);
  const fav = wishlist.includes(activity.id);

  const related = activities.filter((a) => a.id !== activity.id).slice(0, 4);

  const handleBookNow = () => {
    if (!date) { toast.error('Please select a travel date'); return; }
    addToCart({ id: activity.id, title: activity.title, image: activity.image, city: activity.city, packageId: pkg.id, packageName: pkg.name, price: pkg.price, qty, date });
    navigate('/checkout');
  };
  const handleAddToCart = () => {
    if (!date) { toast.error('Please select a travel date'); return; }
    addToCart({ id: activity.id, title: activity.title, image: activity.image, city: activity.city, packageId: pkg.id, packageName: pkg.name, price: pkg.price, qty, date });
    toast.success('Added to cart');
  };

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="container-k pt-3 md:pt-4 text-xs text-gray-500 flex items-center gap-1">
        <Link to="/" className="hover:text-gray-700">Home</Link> /
        <Link to={`/search?city=${activity.city.toLowerCase()}`} className="hover:text-gray-700">{activity.city}</Link> /
        <span className="text-gray-700 line-clamp-1">{activity.title}</span>
      </div>

      {/* Hero gallery */}
      <section className="container-k py-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3">
          <div className="relative md:col-span-3 rounded-xl overflow-hidden aspect-[16/10] bg-gray-100">
            <img src={images[imgIdx]} alt="main" className="w-full h-full object-cover" />
            <button onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)} className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full text-white flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setImgIdx((imgIdx + 1) % images.length)} className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full text-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
            <div className="md:hidden absolute bottom-2 right-2 bg-black/50 text-white text-[11px] px-2 py-0.5 rounded-full">{imgIdx + 1}/{images.length}</div>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-3">
            {images.slice(1, 5).map((im, i) => (
              <button key={i} onClick={() => setImgIdx(i + 1)} className={`rounded-xl overflow-hidden aspect-square bg-gray-100 ${imgIdx === i + 1 ? 'ring-2 ring-xlook-orange' : ''}`}>
                <img src={im} alt={`thumb-${i}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="container-k grid md:grid-cols-[1fr_360px] gap-6 md:gap-10">
        {/* Main */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-3xl font-extrabold text-gray-900">{activity.title}</h1>
              <div className="flex items-center gap-3 mt-2 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold">{activity.rating}</span>
                  <span className="text-gray-500">({activity.reviews.toLocaleString()} reviews)</span>
                </div>
                <span className="text-gray-300">|</span>
                <span className="text-gray-600 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {activity.city}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggleWishlist(activity.id)} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center">
                <Heart className={`w-4 h-4 ${fav ? 'fill-red-500 text-red-500' : 'text-gray-700'}`} />
              </button>
              <button className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center"><Share2 className="w-4 h-4 text-gray-700" /></button>
            </div>
          </div>

          {/* Perks strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
            <div className="p-3 rounded-lg bg-orange-50 text-xs flex items-center gap-2"><Zap className="w-4 h-4 text-xlook-orange" /> Instant confirmation</div>
            <div className="p-3 rounded-lg bg-orange-50 text-xs flex items-center gap-2"><Smartphone className="w-4 h-4 text-xlook-orange" /> Mobile voucher</div>
            <div className="p-3 rounded-lg bg-orange-50 text-xs flex items-center gap-2"><Calendar className="w-4 h-4 text-xlook-orange" /> Flexible dates</div>
            <div className="p-3 rounded-lg bg-orange-50 text-xs flex items-center gap-2"><Shield className="w-4 h-4 text-xlook-orange" /> Best price guarantee</div>
          </div>

          {/* Packages */}
          <div className="mt-8">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">Select a package</h2>
            <div className="space-y-3">
              {packageOptions.map((p) => (
                <button key={p.id} onClick={() => setPkgId(p.id)} className={`w-full text-left p-4 rounded-xl border transition-colors ${pkgId === p.id ? 'border-xlook-orange bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sm md:text-base">{p.name}</div>
                      <ul className="mt-1 space-y-0.5">
                        {p.perks.map((perk) => (
                          <li key={perk} className="text-xs text-gray-600 flex items-center gap-1"><Check className="w-3 h-3 text-green-600" /> {perk}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-extrabold" style={{ color: '#ff5b00' }}>${p.price}</div>
                      {p.originalPrice && <div className="text-[11px] text-gray-400 line-through">${p.originalPrice}</div>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-8">
            <Tabs defaultValue="about">
              <TabsList className="bg-transparent border-b border-gray-100 w-full justify-start rounded-none h-auto p-0 gap-6">
                <TabsTrigger value="about" className="rounded-none border-b-2 border-transparent data-[state=active]:border-xlook-orange data-[state=active]:text-xlook-orange bg-transparent px-0 pb-3">About</TabsTrigger>
                <TabsTrigger value="highlights" className="rounded-none border-b-2 border-transparent data-[state=active]:border-xlook-orange data-[state=active]:text-xlook-orange bg-transparent px-0 pb-3">Highlights</TabsTrigger>
                <TabsTrigger value="policy" className="rounded-none border-b-2 border-transparent data-[state=active]:border-xlook-orange data-[state=active]:text-xlook-orange bg-transparent px-0 pb-3">Policy</TabsTrigger>
              </TabsList>
              <TabsContent value="about" className="pt-4 text-sm text-gray-700 leading-relaxed">
                Experience one of {activity.city}'s most iconic attractions with a seamless e-ticket entry. Skip the line at the counter, present your mobile voucher at the gate, and dive straight into the fun. Great for families, couples, and solo travelers.
              </TabsContent>
              <TabsContent value="highlights" className="pt-4 text-sm text-gray-700">
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5" /> Enjoy fast-track entry with mobile voucher</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5" /> Top-rated by thousands of travelers worldwide</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5" /> Flexible booking with easy date changes</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5" /> Unbeatable price backed by our guarantee</li>
                </ul>
              </TabsContent>
              <TabsContent value="policy" className="pt-4 text-sm text-gray-700 space-y-2">
                <p><strong>Cancellation:</strong> Free cancellation up to 48 hours before the start date.</p>
                <p><strong>Validity:</strong> Voucher valid for 90 days from purchase.</p>
                <p><strong>Age:</strong> Children under 3 enter free with paying adult.</p>
              </TabsContent>
            </Tabs>
          </div>

          {/* Reviews */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg md:text-xl font-bold">Reviews</h2>
              <div className="flex items-center gap-1 text-sm"><Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /><span className="font-semibold">{activity.rating}</span><span className="text-gray-500">/5 · {activity.reviews.toLocaleString()} reviews</span></div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {reviewsList.map((r) => (
                <div key={r.id} className="p-4 rounded-xl border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-8 h-8"><AvatarFallback className="text-xs bg-orange-100 text-orange-700">{r.avatar}</AvatarFallback></Avatar>
                    <div>
                      <div className="text-sm font-semibold">{r.name}</div>
                      <div className="text-[11px] text-gray-500">{r.country} · {r.date}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">{r.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-10">
            <h2 className="text-lg md:text-xl font-bold mb-3">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-sm">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-700">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Related */}
          <div className="mt-10 mb-24 md:mb-10">
            <h2 className="text-lg md:text-xl font-bold mb-3">You might also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {related.map((a) => <ActivityCard key={a.id} activity={a} />)}
            </div>
          </div>
        </div>

        {/* Sticky booking panel desktop */}
        <aside className="hidden md:block">
          <div className="sticky top-28 p-5 rounded-2xl border border-gray-100 shadow-soft bg-white">
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-gray-500">from</span>
              <span className="text-2xl font-extrabold" style={{ color: '#ff5b00' }}>${pkg.price}</span>
              {pkg.originalPrice && <span className="text-sm text-gray-400 line-through">${pkg.originalPrice}</span>}
            </div>
            <div className="text-[11px] text-gray-500 mb-4">per traveler · taxes included</div>

            <label className="block text-xs font-semibold text-gray-700 mb-1">Travel date</label>
            <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm mb-3" />

            <label className="block text-xs font-semibold text-gray-700 mb-1">Travelers</label>
            <div className="flex items-center justify-between h-10 px-2 rounded-lg border border-gray-200 mb-4">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              <span className="text-sm font-semibold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
            </div>

            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-gray-600">Total</span>
              <span className="font-extrabold text-gray-900">${(pkg.price * qty).toFixed(2)}</span>
            </div>

            <Button onClick={handleBookNow} className="w-full rounded-full bg-xlook-orange hover:opacity-90 text-white h-11 font-semibold">Book now</Button>
            <Button onClick={handleAddToCart} variant="outline" className="w-full rounded-full mt-2 h-11 border-gray-300">Add to cart</Button>
          </div>
        </aside>
      </div>

      {/* Sticky mobile booking bar */}
      <div className="md:hidden fixed bottom-16 left-0 right-0 z-30 bg-white border-t border-gray-100 px-4 py-2 flex items-center gap-3">
        <div>
          <div className="text-[10px] text-gray-500">from</div>
          <div className="text-lg font-extrabold leading-none" style={{ color: '#ff5b00' }}>${pkg.price}</div>
        </div>
        <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="flex-1 h-10 px-2 rounded-lg border border-gray-200 text-xs" />
        <Button onClick={handleBookNow} className="rounded-full bg-xlook-orange hover:opacity-90 text-white h-10 px-5 text-sm font-semibold">Book</Button>
      </div>
    </div>
  );
}
